import { ErrorPolicy, WatchQueryFetchPolicy } from '@apollo/client';

import * as graphql from '@treams/treams-network';

export type GrowthPathScale = NonNullable<
	NonNullable<graphql.GrowthPathScaleQuery['viewer']['workspace']>['growthPathScale']
>;

type QueryOptions = {
	skip?: boolean;
	fetchPolicy?: WatchQueryFetchPolicy;
	errorPolicy?: ErrorPolicy;
};

/** The workspace Groeipad scale; undefined when growth paths score on each competency's own levels. */
export function useGrowthPathScale() {
	const query = graphql.useGrowthPathScaleQuery();

	return {
		scale: query.data?.viewer?.workspace?.growthPathScale ?? undefined,
		loading: query.loading && !query.data,
	};
}

export function growthPathScaleLevels(scale: GrowthPathScale) {
	return scale.levels.map((level) => ({ enabled: true, criteria: null, level }));
}

export function useGrowthPathUserCompetenciesQuery(
	variables: Omit<graphql.UserCompetenciesQueryVariables, 'competencyLevelSetID'>,
	options: QueryOptions = {},
) {
	const { scale, loading } = useGrowthPathScale();
	const query = graphql.useUserCompetenciesQuery({
		...options,
		variables: { ...variables, competencyLevelSetID: scale?.competencyLevelSetID },
		skip: options.skip || loading,
	});

	return { ...query, loading: query.loading || loading, scale };
}

export function useGrowthPathFeedbackQuery(
	variables: Omit<graphql.UserGrowthPathFeedbackQueryVariables, 'competencyLevelSetID'>,
	options: QueryOptions = {},
) {
	const { scale, loading } = useGrowthPathScale();
	const query = graphql.useUserGrowthPathFeedbackQuery({
		...options,
		variables: { ...variables, competencyLevelSetID: scale?.competencyLevelSetID },
		skip: options.skip || loading,
	});

	return { ...query, loading: query.loading || loading };
}

export function useGrowthPathJobTitleCompetenciesQuery(
	variables: Omit<graphql.JobTitleCompetenciesQueryVariables, 'competencyLevelSetID'>,
	options: QueryOptions = {},
) {
	const { scale, loading } = useGrowthPathScale();
	const query = graphql.useJobTitleCompetenciesQuery({
		...options,
		variables: { ...variables, competencyLevelSetID: scale?.competencyLevelSetID },
		skip: options.skip || loading,
	});

	return { ...query, loading: query.loading || loading, scale };
}
